import type { MotorHandbookModel } from './motorTechReference';

export type MotorHandbookCopyItem = {
  /** Подзаголовок внутри секции: «Пульт управления», «Цвет» */
  label?: string;
  text: string;
};

export type MotorHandbookCopySection = {
  title: string;
  items: MotorHandbookCopyItem[];
};

/** Крупные блоки технички Onviz — только они становятся заголовками секции. */
export const MOTOR_HANDBOOK_SECTION_TITLES = [
  'Управление и подключение',
  'Внешний вид и конструкция',
  'Производительность',
  'Надежность и защита',
  'Что входит в комплект',
] as const;

const SECTION_BUCKET_RULES: { title: string; re: RegExp }[] = [
  { title: 'Управление и подключение', re: /управлен|пульт|wi-?fi|zigbee|matter|rf\s*433|контакт|rj-?9|порт|smart|tuya|xiaomi|шлюз|подключ/i },
  { title: 'Внешний вид и конструкция', re: /габарит|размер|вес|цвет|корпус|внешн|материал|пластик|металл|мм|длина|ширин|высот/i },
  { title: 'Производительность', re: /мощност|скорост|шум|момент|об\/мин|см\/с|оборот|ток(?!ел)|вт\b|н\*м/i },
  { title: 'Надежность и защита', re: /ip\d|температур|надеж|защит|пыл|влаг|s2\b|режим работ/i },
  { title: 'Что входит в комплект', re: /комплект|входит|кабель|инструкц|руководств|короб/i },
];

function normalizeLine(raw: string): string {
  return String(raw || '')
    .replace(/\u00a0/g, ' ')
    .trim();
}

function stripBullet(line: string): string {
  return normalizeLine(line).replace(/^[•\-*–—]\s*/, '');
}

function isMainSectionTitle(text: string): boolean {
  const t = stripBullet(text).replace(/:$/, '').trim();
  return MOTOR_HANDBOOK_SECTION_TITLES.some((x) => x.toLowerCase() === t.toLowerCase());
}

function matchMainSection(line: string): { title: string; rest: string } | null {
  const s = stripBullet(line);
  if (!s) return null;
  const low = s.toLowerCase();
  for (const title of MOTOR_HANDBOOK_SECTION_TITLES) {
    const tLow = title.toLowerCase();
    if (low === tLow || low === `${tLow}:`) return { title, rest: '' };
    if (low.startsWith(`${tLow}:`)) {
      return { title, rest: s.slice(title.length + 1).trim() };
    }
  }
  return null;
}

function bucketForText(text: string): string {
  const low = text.toLowerCase();
  for (const rule of SECTION_BUCKET_RULES) {
    if (rule.re.test(low)) return rule.title;
  }
  return 'Дополнительно';
}

/** Подзаголовок внутри секции (ПУЛЬТ УПРАВЛЕНИЯ, Питание:, Цвет:). */
function parseSubLabel(line: string): string | null {
  const s = stripBullet(line);
  if (!s || isMainSectionTitle(s)) return null;

  const onlyLabel = s.match(/^([^:]{2,44}):\s*$/);
  if (onlyLabel) return onlyLabel[1].trim();

  const letters = s.replace(/[^a-zA-Zа-яА-ЯёЁ]/g, '');
  if (letters.length >= 3 && letters.length <= 44 && letters === letters.toUpperCase()) {
    return s.replace(/:$/, '').trim();
  }

  return null;
}

function parseLabelValue(line: string): { label: string; text: string } | null {
  const s = stripBullet(line);
  const m = s.match(/^([^:]{2,44}):\s*(.+)$/);
  if (!m) return null;
  const label = m[1].trim();
  const text = m[2].trim();
  if (!text || isMainSectionTitle(label)) return null;
  return { label, text };
}

function bucketItemsIntoSections(items: MotorHandbookCopyItem[]): MotorHandbookCopySection[] {
  const buckets = new Map<string, MotorHandbookCopyItem[]>();
  for (const title of [...MOTOR_HANDBOOK_SECTION_TITLES, 'Дополнительно']) {
    buckets.set(title, []);
  }

  for (const item of items) {
    const hay = `${item.label ?? ''} ${item.text}`;
    const title = bucketForText(hay);
    buckets.get(title)?.push(item);
  }

  const out: MotorHandbookCopySection[] = [];
  for (const title of [...MOTOR_HANDBOOK_SECTION_TITLES, 'Дополнительно']) {
    const list = buckets.get(title) || [];
    if (list.length) out.push({ title, items: list });
  }
  return out;
}

/**
 * Разбор текста: 5 крупных секций + подзаголовки внутри (как эталон MR-2234).
 */
export function parseMotorHandbookCopy(body: string): MotorHandbookCopySection[] {
  const trimmed = String(body || '').trim();
  if (!trimmed) return [];

  const sections: MotorHandbookCopySection[] = [];
  let current: MotorHandbookCopySection | null = null;
  let pendingSubLabel: string | undefined;

  const resetPending = () => {
    pendingSubLabel = undefined;
  };

  const openSection = (title: string) => {
    const existing = sections.find((s) => s.title.toLowerCase() === title.toLowerCase());
    if (existing) {
      current = existing;
      resetPending();
      return;
    }
    current = { title, items: [] };
    sections.push(current);
    resetPending();
  };

  const pushItem = (text: string, label?: string) => {
    const t = text.trim();
    if (!t) return;
    if (!current) openSection(bucketForText(t));
    current!.items.push({ label: label || pendingSubLabel, text: t });
    pendingSubLabel = undefined;
  };

  for (const raw of trimmed.split('\n')) {
    const line = normalizeLine(raw);
    if (!line) continue;

    const main = matchMainSection(line);
    if (main) {
      openSection(main.title);
      if (main.rest) pushItem(main.rest);
      continue;
    }

    const hash = line.match(/^#{1,3}\s+(.+)$/);
    if (hash) {
      const title = hash[1].trim();
      if (isMainSectionTitle(title)) openSection(title.replace(/:$/, ''));
      else pendingSubLabel = title;
      continue;
    }

    const sub = parseSubLabel(line);
    if (sub) {
      pendingSubLabel = sub;
      continue;
    }

    const lv = parseLabelValue(line);
    if (lv) {
      pushItem(lv.text, lv.label);
      continue;
    }

    pushItem(stripBullet(line));
  }

  if (sections.length === 0) return [];

  const onlyMain = sections.every((s) =>
    MOTOR_HANDBOOK_SECTION_TITLES.some((t) => t.toLowerCase() === s.title.toLowerCase()),
  );
  if (onlyMain) return sections;

  const flat: MotorHandbookCopyItem[] = [];
  for (const s of sections) {
    if (isMainSectionTitle(s.title)) {
      for (const item of s.items) flat.push(item);
    } else {
      for (const item of s.items) {
        flat.push({ label: item.label || s.title, text: item.text });
      }
    }
  }
  return bucketItemsIntoSections(flat.filter((i) => i.text));
}

/** Текст по умолчанию — с секциями и подписями «Ключ: значение». */
export function buildDefaultMotorHandbookBody(model: MotorHandbookModel): string {
  const lines: string[] = [];
  if (model.summary?.trim()) {
    lines.push('О модели:', `• ${model.summary.trim()}`, '');
  }

  const buckets = new Map<string, string[]>();
  for (const title of [...MOTOR_HANDBOOK_SECTION_TITLES, 'Дополнительно']) {
    buckets.set(title, []);
  }

  for (const spec of model.specs) {
    const s = String(spec || '').trim();
    if (!s) continue;
    const m = s.match(/^([^:]{2,44}):\s*(.+)$/);
    const bucket = bucketForText(s);
    if (m) {
      buckets.get(bucket)?.push(`${m[1].trim()}: ${m[2].trim()}`);
    } else {
      buckets.get(bucket)?.push(s.replace(/^•\s*/, ''));
    }
  }

  for (const title of [...MOTOR_HANDBOOK_SECTION_TITLES, 'Дополнительно']) {
    const items = buckets.get(title) || [];
    if (!items.length) continue;
    lines.push(`${title}:`);
    for (const item of items) {
      if (item.includes(':')) lines.push(item);
      else lines.push(`• ${item}`);
    }
    lines.push('');
  }

  return lines.join('\n').trim();
}

export function resolveMotorHandbookBody(
  apiCopy: string | undefined,
  model: MotorHandbookModel,
): string {
  const custom = String(apiCopy ?? '').trim();
  if (custom) return custom;
  return buildDefaultMotorHandbookBody(model);
}

export function motorHandbookCopyTemplate(modelName?: string): string {
  const head = modelName?.trim() ? `${modelName}\n\n` : '';
  const sections = MOTOR_HANDBOOK_SECTION_TITLES.map(
    (t) => `${t}:\nПульт управления:\n• `,
  ).join('\n\n');
  return `${head}${sections}`.trim();
}
